import { Color, NotificationStatus, Status } from '@arritech/shared/types';
import {
	formatDateWithIntl,
	formatLongDateLabel,
	formatRelativeTime,
	parseDateValue,
	type DateValue,
} from '@arritech/shared/utils';

export type FieldValue =
	| string
	| number
	| boolean
	| Date
	| Record<string, unknown>
	| null
	| undefined;

export type FieldFormatter =
	| string
	| ((value: unknown) => string)
	| null
	| undefined;

export const toFieldString = (value: FieldValue): string =>
	value === null || value === undefined ? '' : String(value);

// Field values may be boolean/object for non-date field types; only
// string/number/Date/null are meaningful dates, everything else is treated
// as absent.
const toDateValue = (value: FieldValue): DateValue => {
	if (typeof value === 'string' || typeof value === 'number') {
		return value;
	}
	if (value instanceof Date) {
		return value;
	}
	return null;
};

export const parseFieldDate = (value: FieldValue): Date | null => {
	return parseDateValue(toDateValue(value));
};

export const formatLongDate = (
	value: FieldValue,
	empty: string,
	fallback = ''
): string => {
	return formatLongDateLabel(toDateValue(value), {
		empty,
		fallback,
	});
};

export const formatFieldDate = (
	value: FieldValue,
	format: FieldFormatter,
	empty: string,
	fallback = ''
): string => {
	if (value === null || value === undefined || value === '') {
		return empty;
	}
	if (typeof format === 'function') {
		return format(value);
	}

	const stringValue = toFieldString(value);
	if (format === 'raw') {
		return stringValue;
	}

	const date = parseFieldDate(value);
	if (!date) {
		return fallback || stringValue;
	}

	if (format === 'short') {
		return formatDateWithIntl(date, {
			empty,
			fallback: stringValue,
			options: {
				year: 'numeric',
				month: 'short',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
			},
		});
	}

	return formatDateWithIntl(date, {
		empty,
		fallback: stringValue,
		options: {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
		},
	});
};

export const formatTimeAgo = (
	value: FieldValue,
	empty: string,
	fallback = ''
): string => {
	return formatRelativeTime(toDateValue(value), {
		empty,
		fallback,
	});
};

export const formatFieldNumber = (value: FieldValue, empty: string): string => {
	if (value === null || value === undefined || value === '') {
		return empty;
	}
	const numberValue = Number(value);
	if (Number.isNaN(numberValue)) {
		return toFieldString(value);
	}
	return numberValue.toLocaleString();
};

export const resolveStatusColor = (value: string): Color | 'default' => {
	const normalized = value.trim().toLowerCase();
	if (!normalized) {
		return 'default';
	}

	if (normalized === NotificationStatus.SUCCESS) return Color.SUCCESS;
	if (normalized === NotificationStatus.WARNING) return Color.WARNING;
	if (normalized === NotificationStatus.ERROR) return Color.ERROR;
	if (normalized === NotificationStatus.INFO) return Color.INFO;

	if (normalized === 'new') return Color.INFO;
	if (
		[
			'verification rejected',
			'verifications rejected',
			'verifcation rejected',
			'verifcations rejected',
		].includes(normalized)
	) {
		return Color.WARNING;
	}

	if (normalized === Status.ACTIVE) return Color.SUCCESS;
	if (normalized === Status.PENDING || normalized === Status.LOADING) {
		return Color.WARNING;
	}
	if (normalized === Status.INACTIVE) return Color.SECONDARY;

	if (['approved', 'verified', 'resolved'].includes(normalized)) {
		return Color.SUCCESS;
	}
	if (['awaiting', 'in review', 'needs review'].includes(normalized)) {
		return Color.WARNING;
	}
	if (['failed', 'rejected', 'blocked', 'invalid'].includes(normalized)) {
		return Color.ERROR;
	}

	return 'default';
};

export const resolveStatusCssVariable = (value: string): string | null => {
	const color = resolveStatusColor(value);

	if (color === 'default') {
		return null;
	}

	return `var(--color-${color})`;
};
